import { useState, useEffect, useCallback } from 'react';
import { z } from 'zod';
import { apiSchemas, api } from '@verifime/api-definition';
import { TNotificationHandler } from '../../types';

// Infer types from the auto-generated Zod schemas
type TAdverseMedia = z.infer<typeof apiSchemas.AdverseMedia>;
type TCreateAdverseMediaRequest = z.infer<typeof apiSchemas.CreateAdverseMediaRequest>;

export function useAdverseMediaData({
  customerId,
  successNotificationHandler = console.log,
  errorNotificationHandler = console.error,
}: Readonly<{
  customerId: string;
  successNotificationHandler?: TNotificationHandler;
  errorNotificationHandler?: TNotificationHandler;
}>) {
  const [history, setHistory] = useState<TAdverseMedia[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!customerId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const data = await api.getV1adversemediacustomerCustomerId({ params: { customerId } });
      // Sort data chronologically
      const sortedData = data.sort(
        (a, b) =>
          new Date(a.lastUpdatedDatetime).getTime() - new Date(b.lastUpdatedDatetime).getTime(),
      );
      setHistory(sortedData);
    } catch (e) {
      setError(e as Error);
      errorNotificationHandler('Failed to load adverse media history.');
    } finally {
      setIsLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const createRecord = async (newRecord: TCreateAdverseMediaRequest) => {
    try {
      await api.postV1adversemediacustomerCustomerId(newRecord, { params: { customerId } });
      successNotificationHandler('Adverse media record added successfully.');
      // Refetch data after a successful creation to update the history
      await fetchData();
    } catch (err) {
      errorNotificationHandler('Failed to add adverse media record.');
      throw err; // Re-throw to allow the caller to handle it
    }
  };

  return { history, isLoading, error, createRecord };
}
